import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import ProductFilters from "../components/ProductFilters/ProductFilters";
import BottomSheet from "../components/BottomSheet/BottomSheet";
import FilterLenta from "../components/ProductFilters/FilterLenta";
import { FilterTypeFullRenderer } from "../components/ProductFilters/FilterTypesFull";
import { mapFilterType } from "../components/ProductFilters/FilterHelpers";
import ProductCards from "../components/ProductCards/ProductCards";
import mapProduct from "../utils/mapProduct";
import LoadingScreen from "../components/Loading/LoadingScreen";
import { useNavigate } from "react-router-dom";
import "./scss/manager-products-market.scss";

const STATIC_FILTERS = [
  { key: "delivery", label: "Срок доставки", type: "Delivery" },
  { key: "price", label: "Цена", type: "Number" },
  { key: "rating", label: "Рейтинг", type: "Static" }
];

export default function ManagerMarketProductsBlock({ marketId }) {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dynamicFilters, setDynamicFilters] = useState([]);
  const [filters, setFilters] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [showLenta, setShowLenta] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const take = 10;
  const skip = (page - 1) * take;
  const totalPages = Math.ceil(total / take);

  useEffect(() => {
    api.get("/api/manager/filter")
      .then(r => setDynamicFilters(r.data))
      .catch(() => setDynamicFilters([]));
  }, []);

  useEffect(() => {
    load();
  }, [page, search, filters, statusFilter, marketId]);

  async function load() {
    setLoading(true);

    try {
      const dynamic = [];

      dynamicFilters.forEach(df => {
        const value = filters[df.key];
        if (!value) return;

        if (Array.isArray(value) && value.length === 0) return;

        dynamic.push({
          filterId: df.key,
          values: Array.isArray(value) ? value : [value]
        });
      });

      const { data } = await api.post("/api/manager/products/search", {
        marketId,
        search,
        skip,
        take,
        status: statusFilter,
        dynamicFilters: dynamic,
        priceFrom: filters.price?.from,
        priceTo: filters.price?.to,
        ratingFrom: filters.rating,
        delivery: filters.delivery
      });

      setProducts(data.products);
      setTotal(data.total);

    } finally {
      setLoading(false);
    }
  }

  const normalizedDynamicFilters = useMemo(() => {
    return dynamicFilters.map(f => ({
      key: f.key,
      label: f.name,
      type: mapFilterType(f.type),
      values: f.values ?? [],
      meta: f
    }));
  }, [dynamicFilters]);

  const availableFilters = [
    ...STATIC_FILTERS,
    ...normalizedDynamicFilters
  ];

  function handleProductPress(id) {
    navigate(`/manager/product/${id}`);
  }

  return (
    <div className="manager-products-market">

      <div className="manager-products-market__products-header">
        <input
          placeholder="Поиск в магазине"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              setSearch(searchInput.trim());
            }
          }}
          onBlur={() => {
            setPage(1);
            setSearch(searchInput.trim());
          }}
        />
      </div>

      <ProductFilters
        value={filters}
        filters={availableFilters}
        onChange={(next) => {
          setPage(1);
          setFilters(next);
        }}
        onOpenLenta={() => setShowLenta(true)}
        onOpenFilter={(key) => {
          const f = availableFilters.find(x => x.key === key);
          if (f) setActiveFilter(f);
        }}
        sortActive={false}
        onToggleSort={() => {}}
      />

      <div className="manager-products-market__switcher">
        <button
          className={statusFilter === "all" ? "active" : ""}
          onClick={() => setStatusFilter("all")}
        >
          Все
        </button>

        <button
          className={statusFilter === "approved" ? "active" : ""}
          onClick={() => setStatusFilter("approved")}
        >
          Активные
        </button>

        <button
          className={statusFilter === "cancel" ? "active" : ""}
          onClick={() => setStatusFilter("cancel")}
        >
          Заблокированные
        </button>
      </div>

      <div className="manager-products-market__products-list">
        {loading ? (
          <LoadingScreen />
        ) : (
          <ProductCards
            products={products.map(mapProduct)}
            layout="grid"
            showViewAll={false}
            showCount={true}
            count={total}
            sectionTitle="Товары магазина"
            onProductPress={handleProductPress}
          />
        )}
      </div>

      {totalPages > 1 && (
        <div className="manager-products-market__pagination">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={page === i + 1 ? "active" : ""}
              onClick={() => setPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {activeFilter && (
        <BottomSheet
          open={true}
          header={activeFilter.label}
          closeButtomShow={true}
          onClose={() => setActiveFilter(null)}
        >
          <FilterTypeFullRenderer
            filter={activeFilter}
            value={filters[activeFilter.key]}
            onChange={(nextValue) => {
              setFilters(prev => ({
                ...prev,
                [activeFilter.key]: nextValue
              }));
            }}
          />
        </BottomSheet>
      )}

      <BottomSheet
        open={showLenta}
        header="Фильтры"
        closeButtomShow={true}
        onClose={() => setShowLenta(false)}
      >
        <FilterLenta
          filters={availableFilters}
          values={filters}
          onChange={(next) => {
            setPage(1);
            setFilters(next);
          }}
          onOpen={(filter) => {
            setActiveFilter(filter);
            setShowLenta(false);
          }}
          onClear={(key) => {
            const next = { ...filters };
            delete next[key];
            setPage(1);
            setFilters(next);
          }}
        />
      </BottomSheet>

    </div>
  );
}
